/**
 * Производные величины переговоров без запросов (FE-172, FE-173).
 *
 * Здесь **не решается ничего продуктового**: чей ход, что доступно и почему
 * переписка закрыта — считает backend и присылает кодами. Функции ниже только
 * раскладывают коды по ключам i18n и режут ленту событий на группы.
 *
 * Код без перевода не показывается: сырой `DEALER_NO_RESPONSE` на экране —
 * утечка внутренностей (CLAUDE.md §8).
 */

import type {
  CheckLevel,
  CheckProgress,
  DealerAnswer,
  Mandate,
  NegotiationDetail,
  NegotiationListItem,
  NegotiationMessage,
  NegotiationStatus,
  Offer,
  SellerMetric,
} from './types';

/** Статусы, занимающие слот четвёрки. Зеркало `ACTIVE_STATUSES` на backend. */
export const ACTIVE_STATUSES: readonly NegotiationStatus[] = [
  'draft',
  'active',
  'awaiting_user',
  'paused',
];

export function isActive(status: NegotiationStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

/** Раздел списка: активные, завершённые, закрытые (MVP_PRD §22.1). */
export type NegotiationSection = 'active' | 'completed' | 'closed';

export function sectionOf(item: NegotiationListItem): NegotiationSection {
  if (item.status === 'completed') return 'completed';
  if (item.status === 'closed') return 'closed';
  return 'active';
}

export function groupBySection(
  items: NegotiationListItem[],
): Record<NegotiationSection, NegotiationListItem[]> {
  const groups: Record<NegotiationSection, NegotiationListItem[]> = {
    active: [],
    completed: [],
    closed: [],
  };
  for (const item of items) groups[sectionOf(item)].push(item);
  return groups;
}

export function statusKey(status: string): string {
  return `negotiation.status.${status}`;
}

export function phaseKey(phase: string): string {
  return `negotiation.phase.${phase}`;
}

export function nextActorKey(actor: string): string {
  return `negotiation.nextActor.${actor}`;
}

export function awaitingReasonKey(code: string): string {
  return `negotiation.awaiting.${code}`;
}

export function awaitingActionKey(code: string): string {
  return `negotiation.awaitingAction.${code}`;
}

export function closeReasonKey(code: string): string {
  return `negotiation.close.${code}`;
}

export function conditionKey(code: string): string {
  return `negotiation.condition.${code}`;
}

export function questionKey(code: string): string {
  return `negotiation.question.${code}`;
}

export function targetBasisKey(basis: string): string {
  return `negotiation.targetBasis.${basis}`;
}

export function blockedReasonKey(code: string): string {
  return `negotiation.blocked.${code}`;
}

export function answerStatusKey(status: string): string {
  return `negotiation.check.${status}`;
}

export function contradictionKey(code: string): string {
  return `negotiation.check.contradiction.${code}`;
}

export function rejectReasonKey(code: string): string {
  return `negotiation.check.reject.${code}`;
}

/** Ключ i18n подписи строки профиля коммуникации (§37.2). */
export function metricKey(code: string): string {
  return `negotiation.metrics.${code}`;
}

/**
 * Темы, на которые автохаус отказался отвечать, с дословными цитатами (§37.2).
 *
 * Цитата обязательна на сервере (`CHECK` таблицы `dealer_answers`), поэтому
 * отсутствие её здесь означает испорченные данные, а не «отказ без слов»: такая
 * запись пропускается, а не показывается пустой строкой.
 */
export function refusalTopics(metric: SellerMetric): { question_code: string; quote: string }[] {
  const raw = metric.detail.topics;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (item === null || typeof item !== 'object') return [];
    const { question_code: code, quote } = item as { question_code?: unknown; quote?: unknown };
    if (typeof code !== 'string' || typeof quote !== 'string' || quote === '') return [];
    return [{ question_code: code, quote }];
  });
}

/**
 * Расхождения профиля: тема, код формы и **обе** цитаты (§36.5).
 *
 * Проверяется всё: расхождение без двух цитат не показывается вовсе — то же
 * правило, что в блоке проверки, и по той же причине.
 */
export function metricContradictions(metric: SellerMetric): {
  question_code: string;
  code: string;
  previous: { quote: string; at: string };
  current: { quote: string; at: string };
}[] {
  const raw = metric.detail.items;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (item === null || typeof item !== 'object') return [];
    const entry = item as { question_code?: unknown; code?: unknown };
    const previous = side((item as { previous?: unknown }).previous);
    const current = side((item as { current?: unknown }).current);
    if (previous === null || current === null) return [];
    if (typeof entry.question_code !== 'string' || typeof entry.code !== 'string') return [];
    return [{ question_code: entry.question_code, code: entry.code, previous, current }];
  });
}

/**
 * Дословный ответ автохауса о готовности — к осмотру или к медиа (§37.2).
 *
 * `null` — ответа по существу нет. Ярлыка «готов»/«не готов» здесь не бывает:
 * отличить «Begutachtung jederzeit möglich» от «Begutachtung nur nach
 * Kaufvertrag» можно только истолковав фразу, а толкований CarViQ.DE не делает.
 */
export function readinessQuote(metric: SellerMetric): string | null {
  const quote = metric.detail.quote;
  return typeof quote === 'string' && quote !== '' ? quote : null;
}

/** Вопросы, оставшиеся без ответа, — темами (§37.2). */
export function openQuestions(metric: SellerMetric): { question_code: string; status: string }[] {
  const raw = metric.detail.open_questions;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (item === null || typeof item !== 'object') return [];
    const { question_code: code, status } = item as { question_code?: unknown; status?: unknown };
    if (typeof code !== 'string' || typeof status !== 'string') return [];
    return [{ question_code: code, status }];
  });
}

/**
 * Число из `params` метрики. `null` — параметра нет.
 *
 * Backend отдаёт значения числами, но `params` — это `Record<string, unknown>`
 * с сервера, и слепо приводить его типом значило бы поверить в контракт вместо
 * того, чтобы его проверить.
 */
export function metricNumber(metric: SellerMetric, name: string): number | null {
  const value = metric.params[name];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Последний ответ по каждому вопросу уровня (§36.4).
 *
 * `check_answers` приходит историей: расхождение — это две записи по одному
 * вопросу, и первая нужна ровно для того, чтобы показать обе цитаты рядом
 * (§36.5). На экране при этом состояние вопроса **одно** — последнее, и
 * выбирается оно по времени, а не по порядку в массиве: порядок сортировки
 * задаёт сервер, и полагаться на него как на смысл нельзя.
 *
 * Порядок вопросов сохраняется в том виде, в каком их **задал планировщик**:
 * первым пришедший ответ определяет место вопроса в списке. Каталог —
 * приоритет (§36.2), и пересортировка по статусу спрятала бы вниз ровно то,
 * что важнее всего.
 */
export function latestAnswers(answers: DealerAnswer[], level: CheckLevel): DealerAnswer[] {
  const latest = new Map<string, DealerAnswer>();
  for (const answer of answers) {
    if (answer.level !== level) continue;
    const known = latest.get(answer.question_code);
    if (known === undefined || known.created_at <= answer.created_at) {
      latest.set(answer.question_code, answer);
    }
  }
  return [...latest.values()];
}

/** Числитель и знаменатель отвеченности: `answered / задано` (§37.2). */
export function answerCoverage(answers: DealerAnswer[]): { answered: number; asked: number } {
  const asked = answers.filter((item) => item.status !== 'not_asked');
  return {
    answered: asked.filter((item) => item.status === 'answered').length,
    asked: asked.length,
  };
}

/**
 * Обе цитаты расхождения с датами — или `null`.
 *
 * Проверяется **всё**: расхождение без двух цитат не показывается вовсе. То
 * же правило, что на backend (§36.5), и по той же причине: «автохаус себе
 * противоречит» на основании фразы, которой в письме нет, хуже, чем молчание.
 */
export function contradictionOf(answer: DealerAnswer): {
  code: string;
  previous: { quote: string; at: string };
  current: { quote: string; at: string };
} | null {
  const raw = answer.contradiction;
  if (raw === null || typeof raw !== 'object') return null;
  const previous = side(raw.previous);
  const current = side(raw.current);
  if (previous === null || current === null) return null;
  const code = typeof raw.code === 'string' ? raw.code : (answer.contradiction_code ?? '');
  if (code === '') return null;
  return { code, previous, current };
}

function side(value: unknown): { quote: string; at: string } | null {
  if (value === null || typeof value !== 'object') return null;
  const { quote, at } = value as { quote?: unknown; at?: unknown };
  if (typeof quote !== 'string' || quote === '') return null;
  if (typeof at !== 'string') return null;
  return { quote, at };
}

/**
 * Что было отброшено правилом цитаты (§36.4).
 *
 * Форма та же, что у отброшенных полей предложения, и парсер тот же: с
 * сервера приходит `list[dict]`, а не типизированная структура.
 */
export function rejectedAnswers(answer: DealerAnswer): { field: string; reason: string }[] {
  return parseRejected(answer.rejected);
}

/** Действующая версия мандата. `null` — переписка ещё без мандата (§21.3). */
export function currentMandate(detail: NegotiationDetail): Mandate | null {
  return detail.mandates.find((item) => item.is_current) ?? null;
}

/**
 * Предложение, по которому пользователь принимает решение (§22.7).
 *
 * Последнее **действующее предложение с ценой** — пометка «финальное» не
 * требуется (решение заказчика от 12.09.2026): торг идёт от максимума
 * покупателя без запаса, и ответ автохауса с ценой — «согласен» либо «ниже X
 * не отдам» — в обоих случаях повод для решения человека. Предложение без
 * цены решать не по чему: фиксировать как стартовую цену сделки нечего.
 */
export function decidableOffer(detail: NegotiationDetail): Offer | null {
  const priced = detail.offers.filter((item) => item.price !== null && item.status === 'recorded');
  if (priced.length === 0) return null;
  return priced.reduce((latest, item) =>
    item.version_number > latest.version_number ? item : latest,
  );
}

/**
 * Автохаус согласен на условия покупателя: цена не выше его максимума (§22.7).
 *
 * Сравнение двух чисел с сервера, без толкования слов: «согласен» здесь значит
 * ровно «названная сумма не превышает максимум из мандата».
 */
export function dealerAgreed(offer: Offer, mandate: Mandate): boolean {
  return offer.price !== null && offer.price.amount_cents <= mandate.target_price.amount_cents;
}

/**
 * Письмо агента, которым открыт торг по действующему мандату (§21.5).
 *
 * Последнее исходящее письмо агента (`kind = reply`), созданное не раньше
 * согласия с действующим мандатом: по нему экран говорит «Karl написал
 * автохаусу» и когда. `null` — письма ещё нет: модель пишет либо задача ждёт
 * своей очереди.
 */
export function bargainingLetter(detail: NegotiationDetail): NegotiationMessage | null {
  const mandate = currentMandate(detail);
  if (mandate === null) return null;
  const letters = detail.messages.filter(
    (message) =>
      message.direction === 'outbound' &&
      message.kind === 'reply' &&
      message.status !== 'cancelled' &&
      message.created_at >= mandate.consent_at,
  );
  return letters.at(-1) ?? null;
}

/** Вид открытого пункта проверки — зеркало `brief.CheckFindingBrief.kind`. */
export type CheckFindingKind = 'UNANSWERED' | 'REFUSED' | 'UNCLEAR' | 'CONTRADICTION';

export interface CheckFinding {
  code: string;
  kind: CheckFindingKind;
  /** Дословные слова автохауса, если они есть. У неотвеченного — `null`. */
  quote: string | null;
}

/**
 * Открытые пункты проверки — то, на что опирается цена в письме торга (§21.5).
 *
 * То же правило, что у backend'а (`brief.findings_from_answers`): последнее
 * состояние каждого вопроса, только `refused`, `unclear`, `unanswered` и
 * расхождения. Полученный ответ находкой не считается — решать, проблема ли
 * «да, была авария», значило бы оценивать автомобиль (§2).
 */
export function checkFindings(detail: NegotiationDetail): CheckFinding[] {
  const latest = new Map<string, DealerAnswer>();
  for (const answer of detail.check_answers) {
    const known = latest.get(answer.question_code);
    if (known === undefined || known.created_at <= answer.created_at) {
      latest.set(answer.question_code, answer);
    }
  }
  const findings: CheckFinding[] = [];
  for (const answer of latest.values()) {
    const contradiction = contradictionOf(answer);
    if (contradiction !== null) {
      findings.push({
        code: answer.question_code,
        kind: 'CONTRADICTION',
        quote: contradiction.current.quote,
      });
      continue;
    }
    if (answer.status === 'refused' || answer.status === 'unclear') {
      findings.push({
        code: answer.question_code,
        kind: answer.status === 'refused' ? 'REFUSED' : 'UNCLEAR',
        quote: answer.quote,
      });
    } else if (answer.status === 'unanswered') {
      findings.push({ code: answer.question_code, kind: 'UNANSWERED', quote: null });
    }
  }
  return findings;
}

/**
 * Что можно честно сказать о проверке, когда открытых пунктов нет (FE-584).
 *
 * Пустой список находок ещё не значит «автохаус ответил на всё». Он пуст и
 * тогда, когда автохаусу не задали ни одного вопроса (опросник мандата до
 * переписки, переписка без записанных вопросов), и тогда, когда проверка ещё
 * идёт. Сказать «ответил на всё» в этих случаях — утверждение о продавце,
 * которого никто не делал (CLAUDE.md §2: «спросить ≠ проверить»).
 *
 * * `not_started` — хода проверки нет или не задан ни один вопрос;
 * * `in_progress` — вопросы заданы, проверка не завершена;
 * * `all_answered` — проверка завершена, и **каждый** заданный вопрос имеет
 *   ответ. Ответ без дословной цитаты не записывается вовсе (CHECK в
 *   `dealer_answers`), поэтому «ответил» здесь всегда значит «с цитатой»;
 * * `no_open_items` — проверка завершена, но числа не сходятся с «ответил на
 *   всё»: нейтральная формулировка вместо утверждения.
 */
export type CheckCoverage = 'not_started' | 'in_progress' | 'all_answered' | 'no_open_items';

export function checkCoverage(progress: CheckProgress | null): CheckCoverage {
  if (progress === null || progress.asked === 0) return 'not_started';
  if (!progress.complete) return 'in_progress';
  return progress.answered === progress.asked ? 'all_answered' : 'no_open_items';
}

/** Ключ i18n фразы на шаге цены, когда открытых пунктов нет. */
export function checkCoverageKey(coverage: CheckCoverage): string {
  return `negotiation.setup.price.noFindings.${coverage}`;
}

/** Ключ i18n подписи вида находки. */
export function findingKindKey(kind: CheckFindingKind): string {
  return `negotiation.finding.${kind}`;
}

/** Лучшее (наименьшее по цене) действующее предложение. */
export function bestOffer(detail: NegotiationDetail): Offer | null {
  const priced = detail.offers.filter(
    (item) => item.price !== null && (item.status === 'recorded' || item.status === 'accepted'),
  );
  if (priced.length === 0) return null;
  return priced.reduce((best, item) =>
    (item.price?.amount_cents ?? 0) < (best.price?.amount_cents ?? 0) ? item : best,
  );
}

/**
 * Цитаты-основания извлечённого поля.
 *
 * Поле без цитаты в `evidence` **отсутствует**: это не пропуск отрисовки, а
 * правило §23.9 — предложение не додумывается.
 */
export function quoteFor(offer: Offer, field: string): string | null {
  const entry = offer.evidence[field];
  if (entry === null || typeof entry !== 'object') return null;
  const quote = (entry as { quote?: unknown }).quote;
  return typeof quote === 'string' ? quote : null;
}

/** Поля, отброшенные при извлечении. Пользователь видит их, а не догадывается. */
export function rejectedFields(offer: Offer): { field: string; reason: string }[] {
  return parseRejected(offer.evidence.rejected);
}

/**
 * `[{field, reason, quote}]` из недоверенного JSON.
 *
 * Общий разбор для предложения (§23.9) и для ответов проверки (§36.4): форма
 * одна, потому что правило одно — поле без цитаты не заполняется, а причина
 * отказа показывается.
 */
function parseRejected(value: unknown): { field: string; reason: string }[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (item === null || typeof item !== 'object') return [];
    const { field, reason } = item as { field?: unknown; reason?: unknown };
    if (typeof field !== 'string' || typeof reason !== 'string') return [];
    return [{ field, reason }];
  });
}
